import React, { useEffect, useState } from "react";
import PartnerTestimonialsSection from "./PartnerTestimonialsSection";
import ModalForm from "./ModalForm";
import { useNavigate } from "react-router-dom";
import TraitsImage from "../../Assets/behaviour-traits.png";
import PersonalityTraits from "../../Assets/traits-with-human.png";
import BgVector from "../../Assets/bg-vector.jpg";
import { motion } from "framer-motion";

function PartnerPage() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="font-sans text-gray-800"
    >
      {/* Hero Banner */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="relative bg-white text-center py-20 px-4 mt-12 overflow-hidden"
      >
        {/* Background chart image */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 opacity-30"
        >
          <img
            src={BgVector}
            alt="Growth Chart Background"
            className="w-full h-full object-cover"
          />
        </motion.div>

        {/* Overlay content */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative max-w-7xl mx-auto z-10"
        >
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight text-violet-700"
          >
            Start Your Own AI-Powered Personality Assessment Business
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="text-2xl md:text-3xl mb-8 max-w-3xl mx-auto text-gray-700"
          >
            Your branded platform. Ready in 15–20 days. Low investment:
            <span className="font-semibold text-violet-700">
              Starting from only USD 2,000
            </span>
          </motion.p>

          <div className="md:space-x-8">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsModalOpen(true)}
              className="custom-button text-white font-bold text-lg px-10 py-4 rounded-full hover:bg-violet-800 transition duration-300 shadow-md"
            >
              Contact Us Today
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/sample-report")}
              className=" text-violet-800 font-bold text-lg px-8  py-3.5 rounded-full transition duration-300 shadow-md mt-5 md:mt-0 border-2 border-violet-500"

            >
              View Sample Report
            </motion.button>
          </div>
        </motion.div>
      </motion.section>

      {/* The Big Why */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="py-20 max-md:py-12 px-6 max-w-7xl mx-auto"
      >
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-5xl max-md:text-3xl font-extrabold text-center mb-14 max-md:mb-8 text-gray-900 leading-tight"
        >
          Why Start a People Assessment Business?
        </motion.h2>
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.05,
              },
            },
          }}
          viewport={{ once: true }}
          className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
        >
          {[
            "$20B+ global market for hiring & behavioral assessments",
            "Essential tool for every company — across industries",
            "High-margin, recurring revenue model",
            "No product to build. No tech to manage.",
            "Go live in just 15–20 days with a proven platform",
          ].map((item, index) => (
            <motion.div
              key={index}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 },
              }}
              transition={{ duration: 0.3 }}
              whileHover={{ y: -5 }}
              className="bg-white rounded-2xl shadow-md p-6 flex items-start hover:shadow-lg transition-shadow"
            >
              <div className="bg-violet-100 p-3 rounded-full mr-4">
                <svg
                  className="w-4 h-4 text-violet-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.286 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.285-3.957a1 1 0 00-.364-1.118L2.075 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.951-.69l1.286-3.957z" />
                </svg>
              </div>
              <p className="text-lg text-gray-800 font-semibold">{item}</p>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      {/* What You Get */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="bg-white py-20 max-md:py-12 px-6 border-t border-b border-violet-100"
      >
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-7xl mx-auto"
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-5xl max-md:text-3xl font-extrabold text-center text-gray-900 mb-6 leading-tight"
          >
            Everything You Need to Launch Fast
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="text-center font-bold text-lg text-gray-600 max-w-2xl mx-auto mb-12"
          >
            From your own website to marketing tools — it's all set up so you
            can focus on growing, not building.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <ul className="space-y-5">
                {[
                  "Your own branded website",
                  "Fully integrated AI-powered assessment engine",
                  "Admin dashboard & scoring system",
                  "Ready-to-send report templates",
                  "Built-in email delivery and onboarding",
                  "Marketing toolkit & launch playbook",
                  "Optional: API & CRM integrations",
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start"
                  >
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className="bg-violet-100 p-2 rounded-full mr-4 mt-1"
                    >
                      <svg
                        className="w-4 h-4 text-violet-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.286 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.285-3.957a1 1 0 00-.364-1.118L2.075 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.951-.69l1.286-3.957z" />
                      </svg>
                    </motion.div>
                    <span className="text-base font-bold  text-gray-800">
                      {item}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="border-violet-200"
            >
              <div className="overflow-hidden flex items-center justify-center">
                <img
                  src={TraitsImage}
                  alt="Dashboard Preview"
                  className="h-full w-[85%] max-md:w-[80%]"
                />
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.section>

      <motion.section initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="relative py-10 max-md:py-12 px-6 bg-gradient-to-br from-white to-violet-200 items-center justify-center flex flex-col">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-5xl max-md:text-3xl  text-center font-extrabold mb-6"
        >
          What Secrets Does Your Ideal Candidate Hide?
        </motion.h2>
        <motion.p initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          viewport={{ once: true }}
          className="text-xl font-semibold text-gray-600 mb-4 md:mb-8 text-center">
          The traits that predict success might surprise you—see for yourself.
        </motion.p>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate("/sample-report")}
          className=" text-violet-800 font-bold text-lg px-8  py-3.5   rounded-full transition duration-300 shadow-md mt-5 md:mt-0 border-2 border-violet-500"

        >
          View Sample Report
        </motion.button>

      </motion.section>

      {/* Built by Experts */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="relative py-20 max-md:py-12 px-6 bg-gradient-to-br from-white to-violet-50"
      >
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center"
        >
          {/* Trait Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-2xl font-extrabold mb-6"
            >
              15 Key Traits This Assessment Measures
            </motion.h2>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              className="text-base font-semibold text-gray-700 mb-8 max-w-xl"
            >
              These traits are proven indicators of workplace performance,
              adaptability, collaboration, and leadership potential.
            </motion.p>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{
                visible: {
                  transition: {
                    staggerChildren: 0.05,
                  },
                },
              }}
              className="grid grid-cols-2 sm:grid-cols-3 gap-4"
            >
              {[
                "Accountability",
                "Adaptability",
                "Communication",
                "Confidence",
                "Creativity",
                "Empathy & Active Listening",
                "Goal Orientation & Drive",
                "Optimism",
                "Pressure Handling",
                "Problem Solving Skills",
                "Relationship Driven",
                "Resilience & Persistence",
                "Team Player",
                "Thick Skin",
                "Time Management",
              ].map((trait, index) => (
                <motion.div
                  key={index}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 },
                  }}
                  transition={{ duration: 0.3 }}
                  whileHover={{ scale: 1.05 }}
                  className="px-4 py-2 rounded-lg border border-violet-200 bg-violet-100 text-center text-violet-800 font-bold text-sm shadow-sm hover:shadow-md transition"
                >
                  {trait}
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Textual Content */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-5xl max-md:text-3xl text-center font-extrabold text-gray-900 leading-tight mb-6"
            >
              Built by Experts in Human Behavior
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              className="text-lg font-semibold text-gray-700 mb-10"
            >
              Designed by behavioral scientists and I/O psychologists, this
              assessment goes beyond personality. It's a practical,
              research-backed tool that reveals how people truly show up in
              real-world scenarios.
            </motion.p>

            <motion.ul
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{
                visible: {
                  transition: {
                    staggerChildren: 0.2,
                  },
                },
              }}
              className="space-y-6"
            >
              {[
                "Grounded in decades of behavioral science research",
                "Scenario-based, real-life decision-making questions",
                "Assesses 15 traits tied to high performance and resilience",
                "Used by top HR leaders and growing teams worldwide",
              ].map((point, index) => (
                <motion.li
                  key={index}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 },
                  }}
                  transition={{ duration: 0.3 }}
                  className="flex items-start"
                >
                  <span className="inline-flex items-center justify-center bg-violet-100 text-violet-600 rounded-full p-2 mr-4">
                    <svg
                      className="w-4 h-4 text-violet-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.286 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.285-3.957a1 1 0 00-.364-1.118L2.075 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.951-.69l1.286-3.957z" />
                    </svg>
                  </span>
                  <span className="text-base font-bold text-gray-800">
                    {point}
                  </span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Who It's For */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="relative py-28 max-md:py-12 px-6"
      >
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-7xl mx-auto text-center"
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-5xl max-md:text-3xl font-extrabold text-gray-900 mb-6 leading-tight"
          >
            Who Is This Perfect For?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="text-lg text-gray-600 font-semibold mb-16 max-md:mb-10 max-w-2xl mx-auto"
          >
            Whether you're launching, scaling, or adding new revenue streams —
            this platform fits right into your goals.
          </motion.p>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.1,
                },
              },
            }}
            className="grid gap-10 max-md:gap-6 md:grid-cols-2 lg:grid-cols-4"
          >
            {[
              {
                title: "Entrepreneurs launching a new SaaS business",
                subtitle: "",
                icon: (
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m2 0a2 2 0 100-4H7a2 2 0 100 4h2m10 0a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                ),
              },
              {
                title: "HR consultants scaling their digital offerings",
                subtitle: "",
                icon: (
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 00-8 0v4H5l7 7 7-7h-3V7z"
                    />
                  </svg>
                ),
              },
              {
                title: "Recruitment agencies & EdTech platforms",
                subtitle: "",
                icon: (
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 15a4 4 0 004 4h10a4 4 0 004-4v-4H3v4z"
                    />
                  </svg>
                ),
              },
              {
                title: "Coaches & L&D professionals",
                subtitle: "",
                icon: (
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 1.343-3 3v1H5l7 7 7-7h-4v-1c0-1.657-1.343-3-3-3z"
                    />
                  </svg>
                ),
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={{
                  hidden: { opacity: 0, y: 40 },
                  visible: { opacity: 1, y: 0 },
                }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.03 }}
                className="group bg-white px-8 py-6 rounded-3xl border border-violet-200 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 text-left"
              >
                <div className="flex flex-row items-center gap-4  sm:flex-col sm:items-start sm:gap-0">
                  <div className="w-12 h-12 rounded-full bg-violet-600 flex items-center justify-center shadow-md shrink-0">
                    {item.icon}
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1 mt-2 max-md:mt-0 tracking-tight">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600">{item.subtitle}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Real Opportunity */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="py-12 mb-8 max-md:py-10 px-6  border-violet-100"
      >
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-7xl mx-auto"
        >
          {/* Heading & Subheading */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-6"
          >
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-5xl max-md:text-3xl font-extrabold text-gray-900 leading-tight mb-4"
            >
              Massive Demand. <br /> Ready-Made Solution. <br /> Global
              Potential.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              className="text-lg text-gray-600 mt-2"
            >
              Launch your white-labeled SelfSync platform to
            </motion.p>
          </motion.div>

          {/* Content Grid */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ staggerChildren: 0.1 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-16 max-md:gap-4 items-center"
          >
            {/* Left List */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {[
                "Help companies hire smarter",
                "Support team realignment & appraisals",
                "Build leadership pipelines",
                "Offer professional development insights",
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start"
                >
                  <div className="flex-shrink-0 bg-violet-600 text-white p-2 rounded-full shadow-md mr-5">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <p className="text-lg text-gray-800 font-semibold">{item}</p>
                </motion.div>
              ))}
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="rounded-3xl overflow-hidden flex justify-center"
              >
                <img
                  src={PersonalityTraits}
                  alt="Global demand for behavioral assessments"
                  className="w-[400px]  h-[350px] max-md:h-[250px] max-md:w-[250px] rounded-2xl"
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Testimonials */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="">
          <PartnerTestimonialsSection />
        </div>
      </motion.section>

      {/* Final CTA */}
      

      {/* 👉 Modal Form */}
      <ModalForm isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </motion.div>
  );
}

export default PartnerPage;
